import { useUsageLimitReason, useUserInfo } from "@/api/user";
import { EndTrialDialog } from "@/components/billing/EndTrialDialog";
import { AlertBanner } from "@/components/ui/alert-banner";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { useStartPaymentMethodSetup } from "@/hooks/useStartPaymentMethodSetup";
import { isRealNativePlatform } from "@/native/platform";
import * as analytics from "@/utils/analytics";
import { trialDaysRemaining } from "@/utils/trial";
import type { GetSubscriptionResponse } from "@cohesive/api-client";
import { Info } from "lucide-react";
import React, { useState } from "react";

interface FreeTrialBannerProps {
  subscription?: GetSubscriptionResponse;
  // Optional override for the "Manage Billing" action (e.g. open a plan modal).
  // Adding a payment method always routes to checkout, independent of this.
  onManageBillingClick?: () => void;
  // The Plan pane's hero already states the trial, so the banner only speaks up
  // when something needs doing.
  informational?: boolean;
  className?: string;
}

export const FreeTrialBanner: React.FC<FreeTrialBannerProps> = ({
  subscription,
  onManageBillingClick,
  informational = true,
  className = "",
}) => {
  const currentPlan = subscription?.data.stripe_subscription;
  const currentPlanName = currentPlan?.plan_name;
  const currentPlanDisplayName = currentPlan?.plan_display_name;
  const subscriptionStatus = currentPlan?.status;
  const currentPeriodEnd = currentPlan?.current_period_end;
  const openSettings = useOpenSettings();
  const isOrgAdmin = useIsOrgAdmin();
  const { reason } = useUsageLimitReason();
  const { data: userInfo } = useUserInfo();
  const startPaymentMethodSetup = useStartPaymentMethodSetup();
  const [showEndTrialDialog, setShowEndTrialDialog] = useState(false);

  // Check if user is on free trial and has payment method
  const isOnFreeTrial = subscriptionStatus === "trialing";
  // Read the card off the subscription (same source as useUsageLimitReason) so
  // every banner agrees on whether a payment method is on file.
  const hasPaymentMethod = Boolean(currentPlan?.default_payment_method_id);

  // Format trial end date
  const formattedTrialEndDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const daysUntilTrialEnd = trialDaysRemaining(currentPeriodEnd);

  // Billing UI is hidden only in the real iOS container (Apple 3.1.1).
  if (isRealNativePlatform()) return null;

  // Don't render if not on trial
  if (!isOnFreeTrial) return null;

  const userSubscription = userInfo?.subscription;
  const usage = userInfo?.usage;
  const currentPeriodUserCredits = usage?.current_period_user_credit_count || 0;
  const includedCredits = userSubscription?.included_credits_per_seat || 0;

  // Determine content and styling based on usage state
  const getContentAndAction = () => {
    // If usage limits exceeded during trial
    if (reason === "trial_credits_exceeded") {
      // A card already on file means the fix is to end the trial (and start
      // billing), not to collect a payment method.
      const endTrialCopy = hasPaymentMethod
        ? "End your trial now to continue using the app."
        : "Add a payment method to continue using the app.";

      // An org-wide block can trip while this viewer's own count trails the
      // per-seat allowance, so only cite the numbers when they back the claim.
      const citeCredits =
        includedCredits > 0 && currentPeriodUserCredits >= includedCredits;

      const adminContent = citeCredits ? (
        <>
          <strong>Usage limit reached.</strong> You've used{" "}
          {currentPeriodUserCredits} out of {includedCredits} included credits
          during your free trial. {endTrialCopy}
        </>
      ) : (
        <>
          <strong>Usage limit reached.</strong> Your organization has used its
          included credits during the free trial. {endTrialCopy}
        </>
      );

      const memberTail = (
        <>
          Please reach out to an administrator to{" "}
          {hasPaymentMethod ? "end the trial" : "add a payment method"}.
        </>
      );

      const memberContent = citeCredits ? (
        <>
          <strong>Usage limit reached.</strong> Your organization has used{" "}
          {currentPeriodUserCredits} out of {includedCredits} included credits
          during the free trial. {memberTail}
        </>
      ) : (
        <>
          <strong>Usage limit reached.</strong> Your organization has used its
          included credits during the free trial. {memberTail}
        </>
      );

      const content = isOrgAdmin ? adminContent : memberContent;

      let action = undefined;
      if (isOrgAdmin && hasPaymentMethod) {
        action = {
          type: "button" as const,
          text: "End trial now",
          onClick: () => {
            analytics.trackBillingTrialPaymentPromptClicked({
              promptType: "manage_billing",
              daysUntilTrialEnd,
              usageReason: reason,
              organization_id: userInfo?.organization?.id,
            });
            setShowEndTrialDialog(true);
          },
        };
      } else if (isOrgAdmin) {
        action = {
          type: "button" as const,
          text: "Add Payment Method",
          onClick: () => {
            analytics.trackBillingTrialPaymentPromptClicked({
              promptType: "add_payment",
              daysUntilTrialEnd,
              usageReason: reason,
              organization_id: userInfo?.organization?.id,
            });
            startPaymentMethodSetup();
          },
        };
      }

      return {
        variant: "warning" as const,
        icon: Info,
        content,
        action,
      };
    }

    if (!informational && hasPaymentMethod) return null;

    // Normal free trial content
    const content = (
      <>
        You're currently on a free trial of your{" "}
        <strong>{currentPlanDisplayName || currentPlanName} Plan</strong>.{" "}
        {hasPaymentMethod ? (
          <>
            You will be billed starting{" "}
            {formattedTrialEndDate
              ? `on ${formattedTrialEndDate}`
              : "when your trial ends"}
            .
          </>
        ) : (
          <>
            Add payment information to continue using premium features after
            your trial ends
            {formattedTrialEndDate ? ` on ${formattedTrialEndDate}` : ""}.
          </>
        )}
      </>
    );

    let action = undefined;
    if (!hasPaymentMethod) {
      action = {
        type: "button" as const,
        text: "Add Payment Info",
        onClick: () => {
          analytics.trackBillingTrialPaymentPromptClicked({
            promptType: "add_payment",
            daysUntilTrialEnd,
            usageReason: reason,
            organization_id: userInfo?.organization?.id,
          });
          // Proactively adding a card mid-trial keeps the remaining trial days.
          startPaymentMethodSetup({ keepTrial: true });
        },
      };
    } else {
      action = {
        type: "button" as const,
        text: "Manage Billing",
        onClick: () => {
          analytics.trackBillingTrialPaymentPromptClicked({
            promptType: "manage_billing",
            daysUntilTrialEnd,
            usageReason: reason,
            organization_id: userInfo?.organization?.id,
          });
          if (onManageBillingClick) {
            onManageBillingClick();
          } else {
            openSettings(ROUTES.SETTINGS.USAGE_BILLING);
          }
        },
      };
    }

    return {
      variant: "warning" as const,
      icon: Info,
      content,
      action,
    };
  };

  const banner = getContentAndAction();
  if (!banner) return null;

  const { variant, icon, content, action } = banner;

  return (
    <>
      <AlertBanner
        variant={variant}
        icon={icon}
        action={action}
        className={className}
      >
        {content}
      </AlertBanner>
      <EndTrialDialog
        open={showEndTrialDialog}
        onOpenChange={setShowEndTrialDialog}
      />
    </>
  );
};
